import { BookOpen, Code, Terminal, ShieldAlert, Film, Tv, Smartphone, Music } from 'lucide-react';

import { API_URL } from '../config/api';

export default function ApiDocs() {

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-indigo-600" />
          API Documentation
        </h1>
        <p className="text-slate-500 mt-2 text-lg">
          Learn how to fetch content from the HeritageOS API for your client applications (Mobile & Android TV).
        </p>
      </div>

      <div className="space-y-8">

        {/* --- Movies & Series --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Film className="w-5 h-5 text-indigo-500" />
            Get Movies & Series (Android TV)
          </h2>
          <p className="text-slate-600 mb-4">
            Retrieve a list of all available movies and series. Use the <code className="bg-slate-100 px-1 rounded">app_target=tv</code> parameter.
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400 mb-2">
            GET {API_URL}/content/app/stories?app_target=tv&lang=ar
          </div>
          <p className="text-slate-500 text-sm">
            <em>By default, titles are returned in Arabic if requested via <code className="bg-slate-100 px-1 rounded">lang=ar</code>.</em>
          </p>
        </section>

        {/* --- Live TV & Radios --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Tv className="w-5 h-5 text-rose-500" />
            Get Live TV & Radios (Android TV)
          </h2>
          <p className="text-slate-600 mb-4">
            Retrieve active live TV channels and radio stations. You can filter by type (TV or Radio).
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400 mb-2">
            GET {API_URL}/iptv/channels<br/>
            GET {API_URL}/iptv/channels?type=TV<br/>
            GET {API_URL}/iptv/channels?type=Radio<br/>
            GET {API_URL}/iptv/channels?category=News<br/>
            GET {API_URL}/iptv/channels?id=&#123;channel-uuid&#125;
          </div>
        </section>

        {/* --- Stories --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-amber-500" />
            Get Stories (Mobile App)
          </h2>
          <p className="text-slate-600 mb-4">
            Retrieve vertical stories for the mobile app using <code className="bg-slate-100 px-1 rounded">app_target=stories</code>.
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400">
            GET {API_URL}/content/app/stories?app_target=stories
          </div>
        </section>

        {/* --- VOD Filters --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Code className="w-5 h-5 text-slate-500" />
            VOD Query Parameters & Filters
          </h2>
          <p className="text-slate-600 mb-4">
            You can filter and localize VOD content (Movies, Series, Stories) using query parameters.
          </p>
          
          <div className="overflow-x-auto border border-slate-200 rounded-lg mb-6">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-4 py-3 border-b border-slate-200 font-semibold">Parameter</th>
                  <th className="px-4 py-3 border-b border-slate-200 font-semibold">Description</th>
                  <th className="px-4 py-3 border-b border-slate-200 font-semibold">Example</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">lang</td>
                  <td className="px-4 py-3 text-slate-600">Translates titles (e.g., ar, fr, ary, rif, shi)</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?lang=ar</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">category</td>
                  <td className="px-4 py-3 text-slate-600">Filter by exact category name</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?category=Action</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">genre</td>
                  <td className="px-4 py-3 text-slate-600">Filter by exact genre name</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?genre=Drama</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">year</td>
                  <td className="px-4 py-3 text-slate-600">Filter by release year</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?year=2024</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">app_target</td>
                  <td className="px-4 py-3 text-slate-600">Specify app to fetch (stories/tv)</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?app_target=tv</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">kids_mode</td>
                  <td className="px-4 py-3 text-slate-600">Filter to kids-friendly content only</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?kids_mode=true</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">is_kids_friendly</td>
                  <td className="px-4 py-3 text-slate-600">Alias for kids_mode (Stories app)</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?is_kids_friendly=true</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-indigo-600">format</td>
                  <td className="px-4 py-3 text-slate-600">Filter by media format (audio, video, text)</td>
                  <td className="px-4 py-3 font-mono text-slate-500">?format=audio</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* --- Content Details --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-slate-500" />
            Get Content Details & Media Links
          </h2>
          <p className="text-slate-600 mb-4">
            To get full details, episodes, and media stream links (mp4, m3u8, srt, etc.) for a specific movie or series, fetch it by its CUID.
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400 mb-4">
            GET {API_URL}/content/story/:cuid?lang=ar
          </div>
          <p className="text-slate-500 text-sm italic">
            Example: {API_URL}/content/story/26842?lang=ar
          </p>
        </section>

        {/* --- Categories & Genres --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-slate-500" />
            Get Categories and Genres
          </h2>
          <p className="text-slate-600 mb-4">
            To dynamically build UI menus (Home screen tabs, Filters), fetch the exact lists of Categories and Genres specific to the app context.
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400 mb-4">
            GET {API_URL}/content/categories?app_target=tv<br/>
            GET {API_URL}/content/genres?app_target=tv
          </div>
          <p className="text-slate-500 text-sm italic">
            Example JSON Response: <br/>
            <code>[&#123;"id":1,"name":"Action"&#125;, &#123;"id":2,"name":"Drama"&#125;]</code>
          </p>
        </section>

        {/* --- Background Music --- */}
        <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Music className="w-5 h-5 text-fuchsia-500" />
            Get Background Music (Stories App)
          </h2>
          <p className="text-slate-600 mb-4">
            Retrieve the library of background music tracks for stories. You can fetch all music, filter by category, or fetch a specific track by its ID (UUID).
          </p>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto text-emerald-400 mb-4">
            GET {API_URL}/music<br/>
            GET {API_URL}/music?category=Ambient<br/>
            GET {API_URL}/music/:uuid
          </div>
          <p className="text-slate-500 text-sm italic">
            Example JSON Response: <br/>
            <code>[&#123;"uuid": "...", "name": "Epic Intro", "music_url": "https://..."&#125;]</code>
          </p>
        </section>
      </div>
    </div>
  );
}
